import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";

/**
 * Returns the active OTA bundle version, or null on web.
 * - "1.0.42"  — an OTA bundle is active
 * - "native"  — app is running the built-in (store) bundle, no OTA applied yet
 * - null      — running on web, not a native app
 */
export async function getOtaBundleVersion(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { bundle } = await CapacitorUpdater.current();
    if (!bundle || bundle.id === "builtin" || !bundle.version) return "native";
    return bundle.version;
  } catch {
    return null;
  }
}

// ─── Codename generator (must match scripts/codename.js) ──────────────────

const ADJECTIVES = [
  'Amber', 'Arctic', 'Blazing', 'Bright', 'Bronze',
  'Calm', 'Cedar', 'Cobalt', 'Coral', 'Cosmic',
  'Crimson', 'Crystal', 'Dapper', 'Dawn', 'Deep',
  'Dusty', 'Echo', 'Electric', 'Ember', 'Fading',
  'Fierce', 'Fleet', 'Frosty', 'Ghost', 'Gilded',
  'Golden', 'Granite', 'Hazy', 'Hollow', 'Hushed',
  'Indigo', 'Iron', 'Ivory', 'Jade', 'Jolly',
  'Keen', 'Lemon', 'Liquid', 'Lucky', 'Luna',
  'Maple', 'Marble', 'Misty', 'Mossy', 'Neon',
  'Noble', 'Opal', 'Orchid', 'Paper', 'Peach',
  'Phantom', 'Plum', 'Polar', 'Prism', 'Quiet',
  'Rapid', 'Raven', 'Rebel', 'Rocky', 'Royal',
  'Rusty', 'Sable', 'Sage', 'Sandy', 'Scarlet',
  'Shadow', 'Silver', 'Slate', 'Solar', 'Sonic',
  'Spicy', 'Steady', 'Steel', 'Storm', 'Sugar',
  'Swift', 'Tango', 'Tidal', 'Timber', 'Topaz',
  'Turbo', 'Twilight', 'Urban', 'Velvet', 'Violet',
  'Vivid', 'Warm', 'Wild', 'Wired', 'Zen',
];

const NOUNS = [
  'Arrow', 'Banjo', 'Bass', 'Bear', 'Birch',
  'Bloom', 'Bolt', 'Breeze', 'Canyon', 'Cello',
  'Chord', 'Cloud', 'Cobra', 'Comet', 'Crane',
  'Crest', 'Crown', 'Dagger', 'Drift', 'Drum',
  'Dune', 'Eagle', 'Falcon', 'Fern', 'Flame',
  'Flash', 'Flute', 'Fox', 'Frost', 'Gecko',
  'Glyph', 'Groove', 'Gust', 'Hawk', 'Haze',
  'Horn', 'Hydra', 'Ivy', 'Jaguar', 'Jazz',
  'Kite', 'Lance', 'Lark', 'Leaf', 'Lynx',
  'Mango', 'Mars', 'Mesa', 'Mint', 'Moon',
  'Moth', 'Nexus', 'Oasis', 'Onyx', 'Orbit',
  'Otter', 'Owl', 'Palm', 'Panda', 'Peak',
  'Phoenix', 'Pike', 'Pine', 'Pixel', 'Plume',
  'Pulse', 'Quail', 'Riff', 'Robin', 'Rocket',
  'Sage', 'Shark', 'Spark', 'Sphinx', 'Spire',
  'Star', 'Stone', 'Strike', 'Surge', 'Tempo',
  'Thor', 'Tiger', 'Trail', 'Trout', 'Tusk',
  'Vibe', 'Volt', 'Wave', 'Wren', 'Zenith',
];

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/** Deterministic codename from a version string (matches scripts/codename.js). */
export function getCodename(version: string): string {
  const h = hashCode(version);
  const adj = ADJECTIVES[h % ADJECTIVES.length];
  const noun = NOUNS[Math.floor(h / ADJECTIVES.length) % NOUNS.length];
  return `${adj} ${noun}`;
}
